import { themeTypographyResetStyles } from "../website/components/themeTypographyResetStyles";
import { css } from "@emotion/react";
import {
  Alert,
  type AlertProps,
  Button,
  Modal,
  theme,
} from "@minted/minted-components";
import React, { useCallback, useEffect, useRef, type ReactNode } from "react";

export type ConfirmationModalProps = {
  alertPrimaryText?: string;
  alertType?: AlertProps["type"];
  alertSecondaryText?: string;
  cancelButtonText?: string;
  children?: ReactNode;
  displayCloseButton?: boolean;
  isDeleteModal?: boolean;
  justAlert?: boolean;
  onCloseClick: () => void;
  onSubmit: () => void;
  open?: boolean;
  showCancel?: boolean;
  submitButtonText?: string;
};

const modalBodyTextStyles = css`
  ${theme.typeStyles.bodySmallStyles};
`;

const buttonContainerStyles = css`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const ConfirmationModal = ({
  alertPrimaryText = "Are you sure?",
  alertType,
  cancelButtonText = "Cancel",
  children,
  displayCloseButton = false,
  isDeleteModal = false,
  onCloseClick,
  onSubmit,
  open = false,
  showCancel = true,
  submitButtonText = "Yes, Delete",
}: ConfirmationModalProps) => {
  const modalRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<Element | null>(null);

  useEffect(() => {
    if (open) {
      openerRef.current = document.activeElement;

      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 0);
    }

    return () => {
      if (openerRef.current instanceof HTMLElement) {
        openerRef.current.focus();
      }
    };
  }, [open]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter") {
        event.stopPropagation();
        if (showCancel) {
          onCloseClick();
        } else {
          onSubmit();
        }
      }
    },
    [showCancel, onCloseClick, onSubmit],
  );

  return (
    <Modal
      displayCloseButton={displayCloseButton}
      onCloseClick={onCloseClick}
      open={open}
      zIndex={10_001}
    >
      <div css={themeTypographyResetStyles} data-cy="confirmationModal">
        <Alert
          primaryText={alertPrimaryText}
          secondaryText=""
          show
          type={alertType || Alert.types.informative}
        />
        <Modal.Body>
          <div css={modalBodyTextStyles}>{children}</div>
        </Modal.Body>
        <Modal.Footer>
          <div css={buttonContainerStyles}>
            {showCancel && (
              <div data-cy="confirmationCancelButton" onKeyDown={onKeyDown}>
                <Button
                  onClick={onCloseClick}
                  text={cancelButtonText}
                  type={Button.types.tertiary}
                />
              </div>
            )}
            <div data-cy="confirmationSubmitButton" onKeyDown={onKeyDown}>
              <Button
                onClick={onSubmit}
                ref={modalRef}
                submit
                text={submitButtonText}
                type={
                  isDeleteModal
                    ? Button.types.destructive
                    : Button.types.primary
                }
              />
            </div>
          </div>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default ConfirmationModal;
